import { formatDataLayerString } from '@awazevr/mfe-common.contexts.tms'
import type { TmsContextType } from '@awazevr/mfe-common.contexts.tms'
import type {
  CommonSite,
  SearchCriteria,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { OPTIMIZELY_EVENTS } from '@/constants'
import { OptimizelyState } from '@/context/optimizely-decisions-context'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

// opti_track dataLayer labels (Awaze GTM). These are stable string labels, not
// Optimizely metric event keys (those live in OPTIMIZELY_EVENTS).
export const VIEW_MORE_TRACK_LABEL = 'properties_nearby_view_more'
export const CHANGE_DATES_FLEX_TRACK_LABEL =
  'empty_search_button_dates_and_flexibility'
export const NO_RESULTS_DURATION_TRACK_LABEL = 'empty_search_button_duration'
export const NO_RESULTS_FLEXIBILITY_TRACK_LABEL =
  'empty_search_button_flexibility'

// Shared Awaze opti_track dataLayer event. tms is undefined without a provider.
const pushOptiTrack = (
  tms: TmsContextType | undefined,
  trackAction: 'click' | 'impression',
  trackLabel: string,
): void => {
  tms?.updateDataLayer?.('link', 'opti_track', {
    track_action: trackAction,
    track_label: trackLabel,
  })
}

export const trackPropertiesNearbySeen = (
  optimizelyState: OptimizelyState,
  _tms?: TmsContextType | undefined,
): void => {
  // The impression is measured via the Optimizely metric event only; there is
  // no opti_track dataLayer label for the carousel impression.
  void trackClientSideOptimizelyEvent(
    OPTIMIZELY_EVENTS.PROPERTIES_NEARBY_SEEN,
    optimizelyState,
  )
}

export const trackPropertiesNearbyCardClick = (
  optimizelyState: OptimizelyState,
): void => {
  void trackClientSideOptimizelyEvent(
    OPTIMIZELY_EVENTS.PROPERTIES_NEARBY_CARD_CLICK,
    optimizelyState,
  )
}

export const trackViewMoreClick = (tms: TmsContextType | undefined): void => {
  pushOptiTrack(tms, 'click', VIEW_MORE_TRACK_LABEL)
}

export const trackLowResultsSeen = (optimizelyState: OptimizelyState): void => {
  void trackClientSideOptimizelyEvent(
    OPTIMIZELY_EVENTS.LOW_RESULTS_SEEN,
    optimizelyState,
  )
}

export const trackChangeDatesFlexClick = (
  tms: TmsContextType | undefined,
): void => {
  pushOptiTrack(tms, 'click', CHANGE_DATES_FLEX_TRACK_LABEL)
}

export const trackNoResultsDurationClick = (
  tms: TmsContextType | undefined,
): void => {
  pushOptiTrack(tms, 'click', NO_RESULTS_DURATION_TRACK_LABEL)
}

export const trackNoResultsFlexibilityClick = (
  tms: TmsContextType | undefined,
): void => {
  pushOptiTrack(tms, 'click', NO_RESULTS_FLEXIBILITY_TRACK_LABEL)
}

// AS-2776 / AS-2777 SmartPolygon ecommerce events. Duplicates frontend-core's
// DataLayerUtils because those builders aren't exported and no existing event
// emits shop_list_title. Move into frontend-core when the payload is shared; field
// shapes match the canonical builder so payloads won't change.

export const SMARTPOLYGON_SHOP_LIST_TITLE = 'smartpolygon recommendation'

const SMARTPOLYGON_LIST_EVENT = 'ecommerce-product list'
const SMARTPOLYGON_CLICK_EVENT = 'ecommerce-product click'

// Search stores dd-MM-yyyy; reverse to yyyy-MM-dd (an already-ISO value stays).
const isIsoDate = (value: string): boolean =>
  value.length === 10 && value[4] === '-' && value[7] === '-'

const toISODate = (value: string): string =>
  isIsoDate(value) ? value : value.split('-').reverse().join('-')

const buildSmartPolygonSearch = (
  searchCriteria: SearchCriteria,
): Record<string, string> => {
  const {
    adult,
    child,
    destinationCategory,
    infant,
    nights,
    pets,
    range,
    regionName,
    start,
  } = searchCriteria

  return {
    search_destination: regionName ? formatDataLayerString(regionName) : 'all',
    search_destination_category: destinationCategory || 'all',
    search_start_date: (start && toISODate(start)) || 'not selected',
    search_duration: formatDataLayerString(
      nights?.toString() || 'not selected',
    ),
    search_flexibility: formatDataLayerString(
      range?.toString() || 'not selected',
    ),
    search_adults: formatDataLayerString(adult?.toString() || 'not selected'),
    search_children: formatDataLayerString(child?.toString() || 'not selected'),
    search_infants: formatDataLayerString(infant?.toString() || 'not selected'),
    search_pets: formatDataLayerString(pets?.toString() || 'not selected'),
  }
}

// startPosition is the 1-based product_list_position of the first property.
// Keys mirror the canonical frontend-core builder verbatim, including the
// `product_capactiy` spelling GTM already keys on — do not "fix" it.
const buildSmartPolygonProductList = (
  properties: CommonSite[],
  startPosition: number,
): Record<string, string[]> => {
  const mapping: Record<string, string[]> = {
    product_parent_id: [],
    product_parent_name: [],
    product_parent_brand: [],
    product_parent_review_score: [],
    product_parent_location: [],
    product_parent_location_category: [],
    product_sku: [],
    product_name: [],
    product_type: [],
    product_grade: [],
    product_capactiy: [],
    product_bedrooms: [],
    product_bathrooms: [],
    product_pets: [],
    product_key_features: [],
    product_list_availability: [],
    product_list_position: [],
    product_list_price: [],
    product_list_strikethrough_price: [],
    product_list_badge: [],
    product_is_free_cancellation: [],
    product_review_score: [],
  }

  properties.forEach((property, index) => {
    const displayName = property.displayName || ''
    const bedrooms = property.property?.bedrooms ?? ''
    const petCount = property.property?.pets ?? ''
    const sleeps = property.property?.sleeps ?? ''
    const bathrooms = property.property?.bathrooms ?? ''
    const keyFeatures = property.USPs
      ? property.USPs.map((feature) =>
          formatDataLayerString(feature.value),
        ).join(', ')
      : ''
    const displayedPrice = property.price || property.priceFrom || ''

    mapping.product_parent_id.push(property.code || '')
    mapping.product_parent_name.push(formatDataLayerString(displayName))
    mapping.product_parent_brand.push('')
    mapping.product_parent_review_score.push('')
    mapping.product_parent_location.push(
      formatDataLayerString(property.location || ''),
    )
    mapping.product_parent_location_category.push(
      formatDataLayerString(property.rhs3 || ''),
    )
    mapping.product_sku.push(property.code || '')
    mapping.product_name.push(formatDataLayerString(displayName))
    mapping.product_type.push('')
    mapping.product_grade.push(formatDataLayerString(property.grade || ''))
    mapping.product_capactiy.push(formatDataLayerString(sleeps.toString()))
    mapping.product_bedrooms.push(formatDataLayerString(bedrooms.toString()))
    mapping.product_bathrooms.push(formatDataLayerString(bathrooms.toString()))
    mapping.product_pets.push(formatDataLayerString(petCount.toString()))
    mapping.product_key_features.push(keyFeatures)
    mapping.product_list_availability.push(property.startDate ? '1' : '')
    mapping.product_list_position.push((startPosition + index).toString())
    mapping.product_list_price.push(displayedPrice.toString())
    mapping.product_list_strikethrough_price.push(
      (property.wasPrice || '').toString(),
    )
    mapping.product_list_badge.push(
      property.isCustomerChoice ? 'is customer choice' : '',
    )
    mapping.product_is_free_cancellation.push('')
    mapping.product_review_score.push(
      formatDataLayerString((property.averageReviewScore || '').toString()),
    )
  })

  return mapping
}

const pushSmartPolygonEvent = (
  tms: TmsContextType | undefined,
  eventName: string,
  searchCriteria: SearchCriteria,
  properties: CommonSite[],
  listSize: number,
  startPosition: number,
): void => {
  tms?.updateDataLayer?.('link', eventName, {
    shop_list_title: SMARTPOLYGON_SHOP_LIST_TITLE,
    shop_number_of_products: listSize.toString(),
    ...buildSmartPolygonSearch(searchCriteria),
    ...buildSmartPolygonProductList(properties, startPosition),
  })
}

// AS-2776: carousel rendered. `properties` is the displayed set.
export const trackSmartPolygonListSeen = (
  tms: TmsContextType | undefined,
  searchCriteria: SearchCriteria,
  properties: CommonSite[],
): void => {
  if (properties.length === 0) {
    return
  }
  pushSmartPolygonEvent(
    tms,
    SMARTPOLYGON_LIST_EVENT,
    searchCriteria,
    properties,
    properties.length,
    1,
  )
}

// AS-2777: single tile clicked. position is its 1-based slot; listSize the
// number of displayed products.
export const trackSmartPolygonCardClick = (
  tms: TmsContextType | undefined,
  searchCriteria: SearchCriteria,
  property: CommonSite,
  position: number,
  listSize: number,
): void => {
  pushSmartPolygonEvent(
    tms,
    SMARTPOLYGON_CLICK_EVENT,
    searchCriteria,
    [property],
    listSize,
    position,
  )
}
